import { CategorisedStaticBenefits } from '@type/job-add';
import { toSeparatorCase } from '@utils/strings/convertCase';
import { filterSelectedBenefits } from '@components/templates/job-add/edit/functions/jobBenefitsFunctions';

/** Types */
interface GetJobPreviewDataProps {
  getJobFormData: () => ApiSchema<'JobModel3'>;
  apiCareerLevels?: ApiSchema<'OptionItem'>[],
  apiEmploymentTypes?: ApiSchema<'OptionItem'>[],
  apiSpokenLanguages?: ApiSchema<'OptionItem'>[],
  apiSpokenLanguageLevels?: ApiSchema<'OptionItem'>[],
  categorisedStaticBenefits?: CategorisedStaticBenefits[],
  salaryPeriodTrans?: string,
}
type GetJobPreviewData = (props: GetJobPreviewDataProps) => ApiSchema<'JobRead'>;

/**
 * @description Extracts career levels from the form data.
 * - `formCareerLevels` - Career levels from the form data.
 * - `apiCareerLevels` - Career levels from the API.
 */
const extractCareerLevels = (formCareerLevels?: string[], apiCareerLevels?: ApiSchema<'OptionItem'>[]) => {
  if (!formCareerLevels || !apiCareerLevels) return [];
  const rawCareerLevels = apiCareerLevels
    .filter((item) => !!(item?.id && formCareerLevels.includes(item.id)));
  // Return as JobCareerLevelRead schema
  return rawCareerLevels.map((item) => ({
    name: item?.name,
    slug: toSeparatorCase(item?.name ?? ''),
  } as ApiSchema<'JobCareerLevelRead'>));
};

/**
 * @description Extracts employment types from the form data.
 * - `formEmploymentTypes` - Employment types from the form data.
 * - `apiEmploymentTypes` - Employment types from the API.
 */
const extractEmploymentTypes = (formEmploymentTypes?: string[], apiEmploymentTypes?: ApiSchema<'OptionItem'>[]) => {
  if (!formEmploymentTypes || !apiEmploymentTypes) return [];
  const rawEmploymentTypes = apiEmploymentTypes
    .filter((item) => !!(item?.id && formEmploymentTypes.includes(item.id)));
  // Return as string array
  return rawEmploymentTypes.map((item) => item?.name ?? '');
};

/**
 * @description Extracts interview questions from the form data.
 * - `formInterviewQuestions` - Interview questions from the form data.
 */
const extractInterviewQuestions = (formInterviewQuestions?: ApiSchema<'InterviewQuestion'>[]) => {
  if (!formInterviewQuestions) return [];
  // Return as JobQuestionRead schema
  return formInterviewQuestions.map((interviewQuestion, index) => ({
    id: index,
    question: interviewQuestion?.question,
  }));
};

/**
 * @description Extracts locations from the form data.
 * - `formLocations` - Locations from the form data.
 */
const extractLocations = (formLocations?: string[]): ApiSchema<'JobLocationRead'>[] => {
  if (!formLocations) return [];
  // Return as JobLocationRead schema
  return formLocations.map((location) => ({
    name: location,
    slug: toSeparatorCase(location),
  }));
};

/**
 * @description Extracts spoken languages from the form data.
 * - `formLanguages` - Spoken languages from the form data.
 * - `apiSpokenLanguages` - Spoken languages from the API.
 * - `apiSpokenLanguageLevels` - Spoken language levels from the API.
 */
const extractLanguages = (
  formLanguages?: ApiSchema<'SpokenLanguageWrite2'>[],
  apiSpokenLanguages?: ApiSchema<'OptionItem'>[],
  apiSpokenLanguageLevels?: ApiSchema<'OptionItem'>[],
): ApiSchema<'SpokenLanguageRead2'>[] => {
  if (!formLanguages || !apiSpokenLanguages || !apiSpokenLanguageLevels) return [];
  // Return as SpokenLanguageRead schema
  return formLanguages.map((language) => {
    const languageItem = apiSpokenLanguages.find((item) => item.id === language.shortName);
    const languageLevel = apiSpokenLanguageLevels.find((item) => item.id === String(language.level));

    return {
      name: languageItem?.name,
      shortName: language.shortName,
      level: language?.level,
      levelName: languageLevel?.name,
    };
  });
};

/**
 * @description Extracts salary from the form data.
 * - `salaryMin` - Minimum salary.
 * - `salaryMax` - Maximum salary.
 * - `salaryPeriodTrans` - Salary period translation.
 */
const extractSalary = (
  salaryMin?: number,
  salaryMax?: number,
  salaryPeriodTrans?: string,
) => {
  if (!(salaryMin && salaryMax)) return undefined;
  const showMax = salaryMax > salaryMin;
  return `${salaryMin}${showMax ? ` - ${salaryMax}` : ''} ${salaryPeriodTrans || ''}`;
};

/**
 * @description Get job preview data.
 * - `getJobFormData` - Get job form data.
 * - `apiCareerLevels` - Career levels.
 * - `apiEmploymentTypes` - Employment types.
 * - `apiSpokenLanguages` - Spoken languages.
 * - `apiSpokenLanguageLevels` - Spoken language levels.
 */
export const getJobPreviewData: GetJobPreviewData = (props) => {
  const {
    getJobFormData,
    apiCareerLevels,
    apiEmploymentTypes,
    apiSpokenLanguages,
    apiSpokenLanguageLevels,
    categorisedStaticBenefits,
    salaryPeriodTrans,
  } = props;

  // Form data
  const jobFormData = getJobFormData();

  // Extract and mutate data into the correct format
  const locations = extractLocations(jobFormData?.addresses);
  const careerLevels = extractCareerLevels(jobFormData?.careerLevels, apiCareerLevels);
  const employmentTypes = extractEmploymentTypes(jobFormData?.employmentTypes, apiEmploymentTypes);
  const interviewQuestions = extractInterviewQuestions(jobFormData?.interviewQuestions);
  const spokenLanguages = extractLanguages(jobFormData?.spokenLanguages, apiSpokenLanguages, apiSpokenLanguageLevels);
  const salary = extractSalary(jobFormData?.salaryMin, jobFormData?.salaryMax, salaryPeriodTrans);
  const benefits = filterSelectedBenefits(jobFormData?.benefits, categorisedStaticBenefits);

  // Get job preview data
  const jobPreviewData: ApiSchema<'JobRead'> = {
    active: true,
    ...(jobFormData?.title && { title: jobFormData.title }),
    ...(jobFormData?.description && { description: jobFormData.description }),
    ...(jobFormData?.vacancies && { vacancies: Number(jobFormData.vacancies) }),
    ...(jobFormData?.salaryConfidential && { salaryConfidential: true }),
    ...(jobFormData?.workPlacement === 'REMOTE' && { remote: true }),
    ...(jobFormData?.workPlacement === 'PARTIAL_REMOTE' && { partialRemote: true }),
    ...(jobFormData?.interviewStages && { interviewStages: jobFormData.interviewStages }),
    ...(jobFormData?.workSchedule && { workSchedule: jobFormData.workSchedule }),
    ...(salary && !jobFormData?.salaryConfidential && { salary }),
    ...(locations.length > 0 && { locations }),
    ...(spokenLanguages.length > 0 && { spokenLanguages }),
    ...(careerLevels.length > 0 && { careerLevels }),
    ...(employmentTypes.length > 0 && { employmentTypes }),
    ...(interviewQuestions.length > 0 && { questions: interviewQuestions }),
    ...(benefits && benefits.length > 0 && { benefits }),
  };

  return jobPreviewData;
};
